"use client";

import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import type { Croquis } from "@/lib/constants";

// 5,5 s : un plan de masse coté ne se lit pas en 2 s. Le défilement laisse le
// temps de parcourir un croquis avant de passer au suivant.
const AUTOPLAY_DELAY_MS = 5500;

export function CroquisCarousel({ croquis }: { croquis: readonly Croquis[] }) {
  const autoplay = useRef(Autoplay({ delay: AUTOPLAY_DELAY_MS, stopOnInteraction: true }));
  const [api, setApi] = useState<CarouselApi>();
  const [position, setPosition] = useState({ courant: 1, total: croquis.length });
  // Renseigné côté client uniquement : lire matchMedia au render provoquerait
  // une divergence d'hydratation. Le plugin démarre donc toujours, et l'effet
  // l'arrête aussitôt si l'utilisateur a demandé moins d'animations.
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    // Attendre l'API : play()/stop() avant l'initialisation d'embla font
    // planter le plugin (il lit internalEngine sur une instance inexistante).
    if (!api) return;

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      prefersReducedMotion.current = query.matches;
      if (query.matches) autoplay.current.stop();
      else autoplay.current.play();
    };
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, [api]);

  // Le total vient de scrollSnapList et non de croquis.length : avec des
  // diapositives de largeurs différentes, embla peut regrouper plusieurs
  // croquis sous un même cran, et le compteur mentirait.
  useEffect(() => {
    if (!api) return;

    const sync = () =>
      setPosition({ courant: api.selectedScrollSnap() + 1, total: api.scrollSnapList().length });
    sync();
    api.on("select", sync);
    api.on("reInit", sync);
    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  const pause = useCallback(() => autoplay.current.stop(), []);
  const resume = useCallback(() => {
    if (!prefersReducedMotion.current) autoplay.current.reset();
  }, []);

  return (
    <Carousel
      plugins={[autoplay.current]}
      opts={{ loop: true, align: "start" }}
      setApi={setApi}
      className="w-full"
      onMouseEnter={pause}
      onMouseLeave={resume}
      aria-label="Croquis d'étude dessinés à la main"
    >
      <CarouselContent className="-ml-4 md:-ml-6">
        {croquis.map((item) => (
          // basis-auto : la largeur de la diapositive suit celle de l'image.
          // Un cadre de ratio fixe letterboxait le plan de masse, qui est le
          // seul croquis en portrait, avec deux bandes blanches sur les côtés.
          // Hauteur commune et largeur libre : aucun letterbox, et toujours
          // aucun recadrage, ce qu'un plan ne supporterait pas.
          <CarouselItem key={item.id} className="pl-4 md:pl-6 basis-auto">
            <figure>
              <div className="h-[280px] md:h-[420px] w-fit bg-surface border border-border">
                <Image
                  src={item.src}
                  alt={item.alt}
                  width={item.width}
                  height={item.height}
                  sizes="(max-width: 768px) 80vw, 40vw"
                  className="h-full w-auto"
                />
              </div>
              <figcaption className="mt-3 text-sm text-text-muted">{item.legende}</figcaption>
            </figure>
          </CarouselItem>
        ))}
      </CarouselContent>

      {/* Compteur à gauche, flèches à droite : collées l'une à l'autre en bas à
          gauche, elles ne disaient ni où on est ni combien il en reste.
          shadcn code en dur « Previous/Next slide » dans un <span sr-only> :
          aria-label prend le pas sur ce contenu pour le nom accessible. */}
      <div className="mt-8 flex items-center justify-between gap-6">
        <p className="text-sm text-text-muted tabular-nums" aria-live="polite" aria-atomic="true">
          <span className="text-text-primary">{position.courant}</span> / {position.total}
        </p>
        <div className="flex items-center gap-3">
          <CarouselPrevious className="static translate-y-0" aria-label="Croquis précédent" />
          <CarouselNext className="static translate-y-0" aria-label="Croquis suivant" />
        </div>
      </div>
    </Carousel>
  );
}
